import React from "react";

const Privacy: React.FC = () => {
  return (
    <section className="max-w-4xl mx-auto px-6 py-16 text-[#1E1E1E] leading-relaxed">
      <h1 className="text-3xl font-semibold mb-2">Privacy Policy</h1>

      <p className="text-sm mb-8">Last updated: December 28, 2025</p>

      {/* 1. Preamble */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">1. Preamble</h2>

        <p className="mb-3">
          The Website is published and operated by Sophie Guérande, a sole
          trader, domiciled at 20 rue Cujas, 31000 Toulouse, France, and
          registered under SIRET number 83828090700018.
        </p>

        <p className="mb-3">
          This Privacy Policy complies with the following legal provisions:
        </p>

        <ul className="list-disc ml-6 mb-3">
          <li>
            Law No. 78-17 of 6 January 1978 relating to information technology,
            files and civil liberties, as amended by Law No. 2018-493 of 20
            June 2018,
          </li>
          <li>
            Law No. 2004-575 of 21 June 2004 on confidence in the digital
            economy (LCEN),
          </li>
          <li>
            Regulation (EU) 2016/679 of 27 April 2016 on the protection of
            natural persons with regard to the processing of personal data
            and on the free movement of such data (GDPR).
          </li>
        </ul>

        <p className="mb-3">
          As part of her activity, Sophie Guérande processes and collects
          Personal Data from Users who interact with the Website or with the
          services offered.
        </p>

        <p>
          The purpose of this Privacy Policy is to inform the User, as the
          owner of their Personal Data, of the methods and purposes of such
          collection and processing, as well as of the various rights they
          possess. This document may be updated by Sophie Guérande in order
          to remain compliant with regulatory changes and her internal
          practices. Where necessary, the User will be informed of any
          impact such changes may have on the processing of their Personal
          Data.
        </p>
      </section>

      {/* 2. Definitions */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">2. Definitions</h2>

        <p>
          “Sophie Guérande” refers to the Data Controller, who is also the
          Publisher of the Website.
        </p>
        <p>
          The “Client” refers to any professional or private individual who
          has entered into an agreement with Sophie Guérande by signing a
          contract, making a purchase, etc.
        </p>
        <p>
          A “Cookie” refers to a small tracker file placed on the User’s
          device, allowing information to be stored regarding their
          browsing activity on the Website.
        </p>
        <p>
          “Personal Data” refers to data that can identify a person,
          directly or indirectly, within the meaning of the GDPR.
        </p>
        <p>The “Privacy Policy” refers to this document.</p>
        <p>
          The “Data Controller” determines the purposes and means of
          processing Personal Data.
        </p>
        <p>
          The “GDPR” refers to Regulation (EU) 2016/679 of 27 April 2016.
        </p>
        <p>
          The “Website” refers to the website available at any time via
          https://sophieguerande.com
        </p>
        <p>
          The “User” refers to any person browsing the Website and
          consenting to the transmission of their Personal Data within the
          scope defined by this Privacy Policy. A User may become a Client,
          but a Client is not necessarily a User of the Website.
        </p>
      </section>

      {/* 3. Data Controller */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">3. Data Controller</h2>

        <p className="mb-3">
          Sophie Guérande is the Data Controller of all Personal Data
          collected on her Website. She undertakes to use this data
          proportionately and to maintain a simplified processing
          activities register, in compliance with the GDPR.
        </p>

        <p>
          She implements appropriate technical and organisational measures
          to secure the processing and storage of Personal Data. In the
          event of a proven risk of a Personal Data breach, she will
          cooperate with the CNIL and, where necessary, notify the data
          subject of the measures taken.
        </p>
      </section>

      {/* 4. Data Collected */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">
          4. Data Collected — Personal Data
        </h2>

        <p className="mb-3">
          All documents transmitted via the Website for the purpose of
          preparing a quote are strictly confidential. They are viewed only
          by Sophie Guérande and are never shared with third parties unless
          required by law. They are used solely to analyse the content and
          prepare a quote.
        </p>

        <p className="mb-3">
          Personal Data is collected lawfully and transparently, in
          accordance with the GDPR. Certain Personal Data must be collected
          in order to provide tailored information, respond to requests and
          perform services.
        </p>

        <p className="mb-3">
          On this Website, the following Personal Data is collected in the
          “Submit a Document” section for the purpose of preparing a quote:
          Full name / Email address / Phone number / Document to be
          translated.
        </p>

        <p>
          The documents uploaded by the User must only contain lawful
          content and must be free from viruses or malicious code. The User
          remains solely responsible for the content of the uploaded files.
        </p>
      </section>

      {/* 5. Purpose and Legal Basis */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">
          5. Purpose and Legal Basis of Processing
        </h2>

        <p className="mb-3">
          The processing of Personal Data by Sophie Guérande relies on the
          legal bases set out in Article 6 GDPR.
        </p>

        <p className="font-semibold mb-2">5.1 Purposes of Processing</p>

        <ul className="list-disc ml-6 mb-3">
          <li>Responding to requests submitted through the contact form,</li>
          <li>
            Preparing and sending quotes for translation or interpreting
            services,
          </li>
          <li>
            Administrative and accounting management where a contractual
            relationship is established.
          </li>
        </ul>

        <p className="font-semibold mb-2">5.2 Legal Basis</p>

        <ul className="list-disc ml-6">
          <li>
            Performance of pre-contractual measures (Article 6-1-b GDPR) —
            where Personal Data is processed in connection with a request
            for information or a quote.
          </li>
          <li>
            Performance of a contract (Article 6-1-b GDPR) — where the User
            becomes a Client and the data is necessary for providing the
            service.
          </li>
          <li>
            Compliance with legal obligations (Article 6-1-c GDPR) — in
            particular accounting and invoicing obligations.
          </li>
          <li>
            Consent (Article 6-1-a GDPR) — for specific processing
            operations, such as non-essential cookies or optional
            communications. Consent may be withdrawn at any time without
            affecting the lawfulness of prior processing.
          </li>
        </ul>
      </section>

      {/* 6. Data Retention */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">6. Data Retention</h2>

        <p className="mb-3">
          Documents transmitted are stored securely and kept only for the
          time strictly necessary to process the request.
        </p>

        <p className="mb-3">
          Personal Data relating to services performed is retained for 5
          years following completion of the service.
        </p>

        <p>
          Documents transmitted for the preparation of a quote which do not
          lead to a contractual relationship are deleted within one year at
          the latest. Certain data may be retained for longer where legally
          required, for example invoices (6 to 10 years).
        </p>
      </section>

      {/* 7. Recipient and Processors */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">
          7. Recipient and Processors
        </h2>

        <p className="mb-3">
          The Data Controller, Sophie Guérande, is the recipient of the
          Personal Data.
        </p>

        <p className="mb-3">
          Personal Data is hosted by Vercel Inc. in the United States. Emails
          are processed via Yahoo, whose servers may also be located outside
          the European Union. Where such transfers occur, appropriate
          safeguards compliant with the GDPR are implemented to protect the
          User’s Personal Data.
        </p>

        <p>
          The Client is informed of such transfers, which are carried out in
          the context of performing a contract or pre-contractual measures
          (information, contact, quotes). Sophie Guérande does not have
          access to the Client’s banking data.
        </p>
      </section>

      {/* 8. User Rights */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">8. User Rights</h2>

        <p className="mb-3">
          At any time, the User has the following rights regarding the
          processing of their Personal Data:
        </p>

        <ul className="list-disc ml-6 mb-3">
          <li>
            Right of access — to know what Personal Data is held and to
            obtain a copy,
          </li>
          <li>
            Right of rectification — to have inaccurate Personal Data
            corrected or updated,
          </li>
          <li>Right to erasure (“right to be forgotten”),</li>
          <li>
            Right to data portability — to transfer data to another service
            provider,
          </li>
          <li>
            Right to object, particularly to marketing communications,
          </li>
          <li>
            Right to withdraw consent, while previously lawful processing
            remains valid.
          </li>
        </ul>

        <p className="mb-3">
          Users may exercise these rights by email at: sguerande@yahoo.fr.
          The User must submit a clear written request. Sophie Guérande
          commits to responding within a maximum of one month.
        </p>

        <p>
          The User may lodge a complaint with the CNIL (Commission Nationale
          de l’Informatique et des Libertés — https://www.cnil.fr/) if they
          believe their Personal Data has been violated. Such complaints
          must be detailed, justified, and submitted as soon as the User
          becomes aware of the issue.
        </p>
      </section>

      {/* 9. Cookies */}
      <section>
        <h2 className="text-xl font-semibold mb-3">9. Cookies</h2>

        <p className="mb-3">
          Browsing the Website may result in Cookies being placed on the
          User’s device. Cookies allow the processing of statistics and
          traffic information, facilitate browsing, and improve the
          Website’s services. Several types of Cookies exist:
        </p>

        <ul className="list-disc ml-6 mb-3">
          <li>
            Essential Cookies, required for the proper functioning and
            accessibility of the Website. User consent is not required for
            these.
          </li>
          <li>
            Functional Cookies, which store the User’s navigation
            preferences.
          </li>
        </ul>

        <p className="mb-3">
          Other types of Cookies are not essential and may therefore be
          refused or disabled by the User. A cookie banner appears upon
          accessing the Website, allowing the User to choose which Cookies
          to enable. These include:
        </p>

        <ul className="list-disc ml-6 mb-3">
          <li>Analytical Cookies (measure audience and statistics),</li>
          <li>Advertising Cookies (personalised ads),</li>
          <li>
            Social media Cookies (identify traffic from networks such as
            Instagram, Facebook, Twitter, Pinterest and enable content
            sharing).
          </li>
        </ul>

        <p className="mb-3">
          No non-essential Cookies will be stored without the User’s prior
          consent.
        </p>

        <p className="mb-3">
          The User may disable non-essential Cookies directly in their
          browser settings. Any malfunction of the Website following such
          deactivation shall not be considered the responsibility of Sophie
          Guérande.
        </p>

        <p className="mb-3">
          Cookies are considered Personal Data; therefore, Users may
          exercise all rights mentioned in Article 8 (User Rights).
        </p>

        <p className="mb-3">
          The lifespan and purposes of Cookies are provided by the plugin
          or hosting service used by Sophie Guérande.
        </p>

        <p className="mb-3">
          Our website uses only strictly necessary cookies required for
          security and basic functionality. These Cookies are essential for
          the operation of the site and do not require user consent under
          applicable regulations. We do not use analytics, advertising, or
          tracking cookies.
        </p>

        <p className="mb-3">The cookies used by the Site are:</p>

        <table className="w-full border border-gray-300 text-sm">
          <thead>
            <tr>
              <th className="border p-2 text-left">Cookie Name</th>
              <th className="border p-2 text-left">Cookie Function</th>
              <th className="border p-2 text-left">
                Expiration after User’s connection
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border p-2">XSRF-TOKEN</td>
              <td className="border p-2">
                Security cookie used to prevent Cross-Site Request Forgery
                (CSRF) attacks
              </td>
              <td className="border p-2">
                Expires at the end of the session or after a few hours
                (framework-generated)
              </td>
            </tr>
            <tr>
              <td className="border p-2">laravel_session</td>
              <td className="border p-2">
                Maintains the user’s session for basic site functionality
              </td>
              <td className="border p-2">
                Expires at the end of the session
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </section>
  );
};

export default Privacy;




